/**
 * ObjectTracker — detection + tracking + memory layer for the object
 * detector (review 2026-06-06, Kay): instead of classifying every frame
 * independently, associate detections across frames into persistent tracks
 * with a stable identity. Two consequences the reviewer asked for:
 *
 *   1. Class identity persists. A track's class is a majority VOTE over a
 *      recent window, so one odd frame (or a brief misclassification)
 *      does not flip the object's class.
 *   2. Occlusion memory. When a detection disappears (e.g. a hand covers
 *      the object) the track COASTS — it stays alive with its last box for
 *      up to maxCoastMs rather than vanishing and being re-created with a
 *      new id / class.
 *
 * Plus a lightweight hand–object INTERACTION state per track (contact /
 * occluded-by-hand / held), modelling the hand and object together rather
 * than as independent per-frame boxes. Observation only — no intent.
 *
 * All coordinates normalized [0,1]. Pure logic (no DOM); unit-testable.
 */

export interface BBoxN { x: number; y: number; w: number; h: number }

export interface RawDetection {
  label: string;
  labelKo: string;
  score: number;
  bboxNorm: BBoxN;
}

export type InteractionState = 'none' | 'contact' | 'occluded_by_hand' | 'held';

/** 0704 §5.7 object-memory state. */
export type MemoryState =
  | 'visible'
  | 'partially_occluded'
  | 'fully_occluded_predicted'
  | 'reappeared'
  | 'lost';

export interface TrackedObject {
  id: number;
  /** Stabilized (voted) class — does not flip on a single odd frame. */
  label: string;
  labelKo: string;
  /** Last matched detection score. */
  score: number;
  /** Last known box (kept while coasting). */
  bbox: BBoxN;
  /** Active = matched this frame; coasting = unmatched but within maxCoastMs. */
  state: 'active' | 'coasting';
  /** Total detection matches over the track's life. */
  hits: number;
  /** ms since the track was created. */
  ageMs: number;
  /** ms since the last detection match (0 when active). */
  missedMs: number;
  /** Hand–object interaction this frame. */
  interaction: InteractionState;
  // ── §5.7 object-memory fields (additive; always set by ObjectTracker,
  //    optional so synthetic TrackedObject literals elsewhere stay valid) ──
  /** Richer 5-state memory (0704). `state` above is kept for back-compat. */
  memoryState?: MemoryState;
  /** Extrapolated box from last match + velocity (== bbox when visible). */
  predictedPosition?: BBoxN;
  /** 0 = fully visible … 1 = fully occluded/predicted. */
  occlusionScore?: number;
  /** 1 - occlusionScore. */
  visibilityScore?: number;
  /** IoU of this frame's detection match (re-id confidence on reappearance; 0 while coasting). */
  reidentificationScore?: number;
}

export interface HandInput {
  landmarks?: Array<{ x: number; y: number }> | null;
  bbox?: BBoxN | null;
}

interface Track {
  id: number;
  bbox: BBoxN;
  score: number;
  labelVotes: Array<{ label: string; labelKo: string; t: number }>;
  createdT: number;
  lastSeenT: number;
  hits: number;
  contactSinceT: number | null;
  // ── §5.7 memory/prediction bookkeeping ──
  /** Center velocity in normalized units per ms (from consecutive matches). */
  vx: number;
  vy: number;
  /** Matched a detection in the current update() call. */
  matchedThisFrame: boolean;
  /** Whether the track was matched in the PREVIOUS update() call. */
  matchedLastFrame: boolean;
  /** IoU of the current-frame match (0 if unmatched). */
  matchIou: number;
  /** True when the current-frame match followed a coasting gap (reappearance). */
  reappearedThisFrame: boolean;
}

function centerOfBox(b: BBoxN): { x: number; y: number } {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 };
}
function clamp01(x: number): number { return x < 0 ? 0 : x > 1 ? 1 : x; }

function iou(a: BBoxN, b: BBoxN): number {
  const ax2 = a.x + a.w, ay2 = a.y + a.h, bx2 = b.x + b.w, by2 = b.y + b.h;
  const ix = Math.max(0, Math.min(ax2, bx2) - Math.max(a.x, b.x));
  const iy = Math.max(0, Math.min(ay2, by2) - Math.max(a.y, b.y));
  const inter = ix * iy;
  const uni = a.w * a.h + b.w * b.h - inter;
  return uni > 0 ? inter / uni : 0;
}

function pointIn(px: number, py: number, b: BBoxN): boolean {
  return px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h;
}

function votedLabel(votes: Track['labelVotes']): { label: string; labelKo: string } {
  const counts = new Map<string, { n: number; ko: string }>();
  for (const v of votes) {
    const c = counts.get(v.label) ?? { n: 0, ko: v.labelKo };
    c.n++; c.ko = v.labelKo; counts.set(v.label, c);
  }
  let best = '', bestN = -1, bestKo = '';
  for (const [label, c] of Array.from(counts.entries())) {
    if (c.n > bestN) { bestN = c.n; best = label; bestKo = c.ko; }
  }
  return { label: best, labelKo: bestKo };
}

export interface ObjectTrackerOpts {
  /** Min IoU to associate a detection with a track. */
  iouMatch?: number;
  /** How long a track survives without a detection match (occlusion). */
  maxCoastMs?: number;
  /** Window for the class-vote majority. */
  classVoteWindowMs?: number;
  /** Contact sustained beyond this → 'held'. */
  heldMs?: number;
}

export class ObjectTracker {
  private tracks: Track[] = [];
  private nextId = 1;
  private iouMatch: number;
  private maxCoastMs: number;
  private classVoteWindowMs: number;
  private heldMs: number;

  constructor(opts: ObjectTrackerOpts = {}) {
    this.iouMatch = opts.iouMatch ?? 0.3;
    this.maxCoastMs = opts.maxCoastMs ?? 1500;
    this.classVoteWindowMs = opts.classVoteWindowMs ?? 2000;
    this.heldMs = opts.heldMs ?? 1000;
  }

  update(detections: RawDetection[], now: number, hand?: HandInput): TrackedObject[] {
    // Reset per-frame match bookkeeping (§5.7); carry over last-frame match.
    for (const t of this.tracks) {
      t.matchedLastFrame = t.matchedThisFrame;
      t.matchedThisFrame = false; t.matchIou = 0; t.reappearedThisFrame = false;
    }
    // ── Greedy IoU association (best pairs first), identity-preserving:
    // a detection can match a track of a DIFFERENT label — we then add a
    // vote rather than relabelling, so the class can only drift by majority.
    const pairs: Array<{ ti: number; di: number; iou: number }> = [];
    for (let ti = 0; ti < this.tracks.length; ti++) {
      for (let di = 0; di < detections.length; di++) {
        const ov = iou(this.tracks[ti].bbox, detections[di].bboxNorm);
        if (ov >= this.iouMatch) pairs.push({ ti, di, iou: ov });
      }
    }
    pairs.sort((a, b) => b.iou - a.iou);
    const usedT = new Set<number>(), usedD = new Set<number>();
    for (const p of pairs) {
      if (usedT.has(p.ti) || usedD.has(p.di)) continue;
      usedT.add(p.ti); usedD.add(p.di);
      const t = this.tracks[p.ti]; const d = detections[p.di];
      const gapMs = now - t.lastSeenT;
      // Center velocity from consecutive matches (normalized units / ms).
      if (gapMs > 0) {
        const cOld = centerOfBox(t.bbox); const cNew = centerOfBox(d.bboxNorm);
        t.vx = (cNew.x - cOld.x) / gapMs; t.vy = (cNew.y - cOld.y) / gapMs;
      }
      t.reappearedThisFrame = !t.matchedLastFrame; // matched after coasting a frame
      t.matchedThisFrame = true; t.matchIou = p.iou;
      t.bbox = d.bboxNorm; t.score = d.score; t.lastSeenT = now; t.hits++;
      t.labelVotes.push({ label: d.label, labelKo: d.labelKo, t: now });
    }
    // Unmatched detections → new tracks.
    for (let di = 0; di < detections.length; di++) {
      if (usedD.has(di)) continue;
      const d = detections[di];
      this.tracks.push({
        id: this.nextId++, bbox: d.bboxNorm, score: d.score,
        labelVotes: [{ label: d.label, labelKo: d.labelKo, t: now }],
        createdT: now, lastSeenT: now, hits: 1, contactSinceT: null,
        vx: 0, vy: 0, matchedThisFrame: true, matchedLastFrame: true,
        matchIou: 1, reappearedThisFrame: false,
      });
    }
    // Prune stale votes + drop tracks that have coasted past maxCoastMs.
    const voteCut = now - this.classVoteWindowMs;
    this.tracks = this.tracks.filter(t => {
      t.labelVotes = t.labelVotes.filter(v => v.t >= voteCut);
      if (t.labelVotes.length === 0) {
        // keep the most recent vote so a coasting track retains its class
        return now - t.lastSeenT <= this.maxCoastMs;
      }
      return now - t.lastSeenT <= this.maxCoastMs;
    });

    // ── Hand bbox (from landmarks if no explicit bbox).
    let handBox: BBoxN | null = hand?.bbox ?? null;
    const lms = hand?.landmarks ?? null;
    if (!handBox && lms && lms.length > 0) {
      const xs = lms.map(p => p.x), ys = lms.map(p => p.y);
      const x = Math.min(...xs), y = Math.min(...ys);
      handBox = { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
    }

    // ── Emit + per-track interaction state.
    return this.tracks.map(t => {
      const missedMs = now - t.lastSeenT;
      const active = missedMs < 1;
      const vl = votedLabel(t.labelVotes);

      // Hand overlaps the box: any hand landmark inside, or hand bbox IoU>0.
      let handOverlap = false;
      if (lms && lms.length > 0) {
        for (const p of lms) if (pointIn(p.x, p.y, t.bbox)) { handOverlap = true; break; }
      }
      if (!handOverlap && handBox) handOverlap = iou(handBox, t.bbox) > 0;

      // Held requires the object to be VISIBLE (active) while the hand is on
      // it; sustained contact promotes to 'held'.
      if (active && handOverlap) t.contactSinceT = t.contactSinceT ?? now;
      else t.contactSinceT = null;

      let interaction: InteractionState = 'none';
      if (active && handOverlap) {
        const contactMs = t.contactSinceT != null ? now - t.contactSinceT : 0;
        interaction = contactMs >= this.heldMs ? 'held' : 'contact';
      } else if (!active && handOverlap) {
        // Coasting (no detection this frame) while a hand sits over the last
        // box → the hand is most likely occluding the object. Keep the class.
        interaction = 'occluded_by_hand';
      }

      // ── §5.7 memory + prediction ──
      const c = centerOfBox(t.bbox);
      const predictedPosition: BBoxN = active
        ? t.bbox
        : {
            x: clamp01(c.x + t.vx * missedMs - t.bbox.w / 2),
            y: clamp01(c.y + t.vy * missedMs - t.bbox.h / 2),
            w: t.bbox.w, h: t.bbox.h,
          };
      let occlusionScore: number;
      if (interaction === 'occluded_by_hand') occlusionScore = 0.7;
      else if (active) occlusionScore = handOverlap ? 0.3 : 0;
      else occlusionScore = clamp01(missedMs / this.maxCoastMs);
      let memoryState: MemoryState;
      if (active && t.reappearedThisFrame) memoryState = 'reappeared';
      else if (active) memoryState = 'visible';
      else if (handOverlap) memoryState = 'partially_occluded';
      else if (missedMs < this.maxCoastMs * 0.7) memoryState = 'fully_occluded_predicted';
      else memoryState = 'lost';
      const reidentificationScore = t.matchedThisFrame ? t.matchIou : 0;

      return {
        id: t.id,
        label: vl.label, labelKo: vl.labelKo,
        score: t.score, bbox: t.bbox,
        state: active ? 'active' : 'coasting',
        hits: t.hits, ageMs: now - t.createdT, missedMs,
        interaction,
        memoryState,
        predictedPosition,
        occlusionScore,
        visibilityScore: 1 - occlusionScore,
        reidentificationScore,
      } as TrackedObject;
    });
  }

  reset(): void {
    this.tracks = [];
    this.nextId = 1;
  }
}
